import { Outlet } from 'react-router-dom';
import { MainLayout } from '@sphere/shared/ui/design-system';
import { useKeycloak } from '@sphere/auth/data-access';
import { useAuthStore } from '@sphere/auth/data-access';
import {
  useAdminStore,
} from '@sphere/shared/data-access/store';
import { useEstablishments } from '@sphere/school/data-access';
import { UserRole } from '@sphere/auth/domain';
import { useMemo, useCallback } from 'react';
import { useTopBarNotifications } from '@/app/useTopBarNotifications';

/**
 * RootLayout - Layout principal de l'application
 *
 * Utilise le composant MainLayout moderne avec TopBar + Sidebar.
 * Récupère automatiquement les données utilisateur depuis Keycloak et le store.
 */
const RootLayout = () => {
  const { logout, tokenParsed, userInfo } = useKeycloak();
  const user = useAuthStore((state) => state.user);
  const { currentEstablishment, setCurrentEstablishment } = useAdminStore();
  const { data: establishments } = useEstablishments({
    enabled: !!user, // Fetch only if user is logged in
  });
  const { notifications, unreadCount, onNotificationClick } =
    useTopBarNotifications();

  // Mapper le rôle utilisateur au format attendu par le Sidebar
  const getUserRole = useCallback((): UserRole => {
    // Essayer d'abord depuis le user store
    if (user?.role && Object.values(UserRole).includes(user.role)) {
      return user.role;
    }

    // Sinon depuis Keycloak (userInfo ou tokenParsed)
    const keycloakRoles =
      userInfo?.realmRoles || tokenParsed?.realm_access?.roles || [];

    // Chercher un rôle correspondant dans nos UserRole
    // On normalise en minuscule pour la comparaison
    const foundRole = keycloakRoles
      .map((r) => r.toLowerCase())
      .find((r): r is UserRole =>
        Object.values(UserRole).includes(r as UserRole)
      );

    if (foundRole) {
      return foundRole;
    }

    // Par défaut
    return UserRole.TEACHER;
  }, [user?.role, userInfo, tokenParsed]);

  // Récupérer les infos utilisateur depuis Keycloak si le store est vide
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const userInfoData = user
    ? {
      id: user.id || tokenParsed?.sub || '',
      firstName:
        user.firstName ||
        userInfo?.firstName ||
        tokenParsed?.given_name ||
        'Utilisateur',
      lastName:
        user.lastName || userInfo?.lastName || tokenParsed?.family_name || '',
      email: user.email || userInfo?.email || tokenParsed?.email || '',
      role: getUserRole(),
    }
    : userInfo
      ? {
        id: userInfo.id || tokenParsed?.sub || '',
        firstName: userInfo.firstName || 'Utilisateur',
        lastName: userInfo.lastName || '',
        email: userInfo.email || '',
        role: getUserRole(),
      }
      : tokenParsed
        ? {
          id: tokenParsed.sub || '',
          firstName: tokenParsed.given_name || 'Utilisateur',
          lastName: tokenParsed.family_name || '',
          email: tokenParsed.email || '',
          role: getUserRole(),
        }
        : {
          id: '',
          firstName: 'Utilisateur',
          lastName: '',
          email: '',
          role: getUserRole(),
        };

  const topBarProps = useMemo(
    () => ({
      user: userInfoData,
      // Contexte pédagogique (Établissement seulement pour le moment)
      context: {
        establishment: currentEstablishment
          ? { id: currentEstablishment.id, name: currentEstablishment.name }
          : undefined,
        // TODO: Ajouter schoolYear dynamiquement si besoin
        // schoolYear: { id: '2024', label: '2024-2025' },
      },
      // Options de contexte
      contextOptions: {
        establishments: establishments?.map((e) => ({
          id: e.id,
          name: e.name,
        })),
        // schoolYears: [],
      },
      // Gestion du changement de contexte
      onContextChange: (
        type: 'establishment' | 'schoolYear' | 'class' | 'subject',
        value: any
      ) => {
        if (type === 'establishment') {
          // Retrouver l'établissement complet
          const selected = establishments?.find((e) => e.id === value.id);
          if (selected) {
            setCurrentEstablishment(selected);
            // Optionnel : Recharger la page ou invalider des requêtes si nécessaire
            // window.location.reload();
          }
        }
      },
      notifications,
      unreadCount,
      onNotificationClick,
      onOpenSearch: () => {
        // TODO: Implémenter la Command Palette
        console.log('Open search');
      },
      onLogout: logout,
    }),
    [
      userInfoData,
      logout,
      currentEstablishment,
      establishments,
      setCurrentEstablishment,
      notifications,
      unreadCount,
      onNotificationClick,
    ]
  );

  const sidebarProps = useMemo(
    () => ({
      userRole: getUserRole(),
      currentEstablishment: currentEstablishment
        ? { id: currentEstablishment.id, name: currentEstablishment.name }
        : null,
      availableEstablishments: establishments?.map((e) => ({
        id: e.id,
        name: e.name,
      })),
      onEstablishmentChange: (id: string) => {
        const selected = establishments?.find((e) => e.id === id);
        if (selected) {
          setCurrentEstablishment(selected);
        }
      },
    }),
    [
      getUserRole,
      currentEstablishment,
      establishments,
      setCurrentEstablishment,
      user?.role,
      userInfo,
      tokenParsed,
    ]
  );

  return (
    <MainLayout topBarProps={topBarProps} sidebarProps={sidebarProps}>
      <Outlet />
    </MainLayout>
  );
};

export default RootLayout;
